/**
 * VolumetricFire for Three.js r177
 * Based on original VolumetricFire by yomotsu
 * Updated for modern Three.js with proper texture loading
 */

import * as THREE from 'three'
import Experience from '../../../Experience'

export default class VolumetricFire {
  constructor(width = 2, height = 3, depth = 2, sliceSpacing = 0.1) {
    this.experience = new Experience()
    this.scene = this.experience.scene
    this.resources = this.experience.resources
    this.time = this.experience.time
    this.debug = this.experience.debug

    // カメラの参照を正しく設定
    this.camera = this.experience.camera.instance;
    this.width = width;
    this.height = height;
    this.depth = depth;
    this.sliceSpacing = sliceSpacing;
    
    // Corner positions and texture coordinates
    this.initCorners();
    
    // Initialize material and geometry
    this.initMaterial();
    this.initGeometry();
    
    // Create mesh
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.frustumCulled = false;
    
    // シーンに追加
    this.scene.add(this.mesh);
    
    // Animation properties
    this.viewVector = new THREE.Vector3();
    
    // Slicing properties
    this.points = [];
    this.texCoords = [];
    this.indexes = [];

    this.setDebug()

    this.time.on('tick', () => {
      this.update()
    })
  }
  
  initCorners() {
    const widthHalf = this.width * 0.5;
    const heightHalf = this.height * 0.5;
    const depthHalf = this.depth * 0.5;
    
    this.posCorners = [
      new THREE.Vector3(-widthHalf, -heightHalf, -depthHalf),
      new THREE.Vector3(widthHalf, -heightHalf, -depthHalf),
      new THREE.Vector3(-widthHalf, heightHalf, -depthHalf),
      new THREE.Vector3(widthHalf, heightHalf, -depthHalf),
      new THREE.Vector3(-widthHalf, -heightHalf, depthHalf),
      new THREE.Vector3(widthHalf, -heightHalf, depthHalf),
      new THREE.Vector3(-widthHalf, heightHalf, depthHalf),
      new THREE.Vector3(widthHalf, heightHalf, depthHalf)
    ];
    
    this.texCorners = [
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(1, 0, 0),
      new THREE.Vector3(0, 1, 0),
      new THREE.Vector3(1, 1, 0),
      new THREE.Vector3(0, 0, 1),
      new THREE.Vector3(1, 0, 1),
      new THREE.Vector3(0, 1, 1),
      new THREE.Vector3(1, 1, 1)
    ];
  }
  
  initMaterial() {
    // Load textures
    const textureLoader = new THREE.TextureLoader();
    
    //TODO: Resources で実装してもいいかもね
    // Load noise texture
    const nzwTexture = this.resources.items['volumetricFirePerlinTexture'];
    nzwTexture.wrapS = THREE.RepeatWrapping;
    nzwTexture.wrapT = THREE.RepeatWrapping;
    nzwTexture.magFilter = THREE.LinearFilter;
    nzwTexture.minFilter = THREE.LinearFilter;
    
    // Load fire profile texture
    const fireTexture = this.resources.items['volumetricFireTexture'];
    fireTexture.wrapS = THREE.ClampToEdgeWrapping;
    fireTexture.wrapT = THREE.ClampToEdgeWrapping;
    fireTexture.magFilter = THREE.LinearFilter;
    fireTexture.minFilter = THREE.LinearFilter;
    
    // Vertex shader
    const vertexShader = `
      attribute vec3 position;
      attribute vec3 tex;
      uniform mat4 projectionMatrix;
      uniform mat4 modelViewMatrix;
      
      varying vec3 texOut;
      
      void main() {
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        texOut = tex;
      }
    `;
    
    // Fragment shader
    const fragmentShader = `
      precision highp float;
      
      // Modified Blum Blum Shub pseudo-random number generator
      vec2 mBBS(vec2 val, float modulus) {
        val = mod(val, modulus);
        return mod(val * val, modulus);
      }
      
      uniform sampler2D nzw;
      const float modulus = 61.0;
      
      // Modified noise function
      float mnoise(vec3 pos) {
        float intArg = floor(pos.z);
        float fracArg = fract(pos.z);
        vec2 hash = mBBS(intArg * 3.0 + vec2(0, 3), modulus);
        vec4 g = vec4(
          texture2D(nzw, vec2(pos.x, pos.y + hash.x) / modulus).xy,
          texture2D(nzw, vec2(pos.x, pos.y + hash.y) / modulus).xy
        ) * 2.0 - 1.0;
        
        return mix(
          g.x + g.y * fracArg,
          g.z + g.w * (fracArg - 1.0),
          smoothstep(0.0, 1.0, fracArg)
        );
      }
      
      // Turbulence function
      float turbulence(vec3 pos) {
        float sum = 0.0;
        float freq = 1.0;
        float amp = 1.0;
        
        for (int i = 0; i < 4; i++) {
          sum += abs(mnoise(pos * freq)) * amp;
          freq *= 2.0;
          amp *= 0.5;
        }
        
        return sum;
      }
      
      const float magnitude = 1.3;
      uniform float time;
      uniform sampler2D fireProfile;
      
      // Sample fire function
      vec4 sampleFire(vec3 loc, vec4 scale) {
        // Convert xz to [-1.0, 1.0] range
        loc.xz = loc.xz * 2.0 - 1.0;
        
        // Convert to (radius, height) to sample fire profile texture
        vec2 st = vec2(sqrt(dot(loc.xz, loc.xz)), loc.y);
        
        // Convert loc to 'noise' space
        loc.y -= time * scale.w;
        loc *= scale.xyz;
        
        // Offset vertical texture lookup
        float offset = sqrt(st.y) * magnitude * turbulence(loc);
        st.y += offset;
        
        if (st.y > 1.0) {
          return vec4(0, 0, 0, 1);
        }
        
        vec4 result = texture2D(fireProfile, st);
        
        // Fade out bottom
        if (st.y < 0.1) {
          result *= st.y / 0.1;
        }
        
        return result;
      }
      
      varying vec3 texOut;
      
      void main() {
        vec3 color = sampleFire(texOut, vec4(1.0, 2.0, 1.0, 0.5)).xyz;
        gl_FragColor = vec4(color * 1.5, 1.0);
      }
    `;
    
    // Create material
    this.material = new THREE.RawShaderMaterial({
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      uniforms: {
        nzw: { value: nzwTexture },
        fireProfile: { value: fireTexture },
        time: { value: 0.0 }
      },
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false
    });
  }
  
  initGeometry() {
    // Create geometry with enough vertices for slicing
    const maxVertices = (this.width + this.height + this.depth) * 30;
    
    const indices = new Uint16Array(maxVertices * 3);
    const positions = new Float32Array(maxVertices * 3);
    const texCoords = new Float32Array(maxVertices * 3);
    
    this.geometry = new THREE.BufferGeometry();
    this.geometry.setIndex(new THREE.BufferAttribute(indices, 1));
    this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.geometry.setAttribute('tex', new THREE.BufferAttribute(texCoords, 3));
    
    // Mark as dynamic
    this.geometry.index.setUsage(THREE.DynamicDrawUsage);
    this.geometry.attributes.position.setUsage(THREE.DynamicDrawUsage);
    this.geometry.attributes.tex.setUsage(THREE.DynamicDrawUsage);
  }
  
  // update(deltaTime) {
  //   this.time += deltaTime;
  //   this.updateViewVector();
  //   this.slice();
  //   this.updateGeometry();
  //   this.material.uniforms.time.value = this.time;
  // }
  
  updateViewVector() {
    // カメラが存在するかチェック
    if (!this.camera || !this.camera.matrixWorldInverse) {
      return;
    }

    const modelViewMatrix = new THREE.Matrix4();
    modelViewMatrix.multiplyMatrices(
      this.camera.matrixWorldInverse,
      this.mesh.matrixWorld
    );
    
    this.viewVector.set(
      -modelViewMatrix.elements[2],
      -modelViewMatrix.elements[6],
      -modelViewMatrix.elements[10]
    ).normalize();
  }
  
  slice() {
    this.points = [];
    this.texCoords = [];
    this.indexes = [];
    
    // Calculate corner distances
    const cornerDistance = [];
    let maxCorner = 0;
    let minDistance = Infinity;
    let maxDistance = -Infinity;
    
    for (let i = 0; i < 8; i++) {
      cornerDistance[i] = this.posCorners[i].dot(this.viewVector);
      
      if (cornerDistance[i] > maxDistance) {
        maxCorner = i;
        maxDistance = cornerDistance[i];
      }
      
      if (cornerDistance[i] < minDistance) {
        minDistance = cornerDistance[i];
      }
    }
    
    // Simplified slicing algorithm
    // Create slices from back to front
    let sliceDistance = Math.floor(maxDistance / this.sliceSpacing) * this.sliceSpacing;
    
    while (sliceDistance > minDistance) {
      this.createSlice(sliceDistance, cornerDistance);
      sliceDistance -= this.sliceSpacing;
    }
  }
  
  createSlice(sliceDistance, cornerDistance) {
    const sliceVertices = [];
    const sliceTexCoords = [];
    
    // Simple quad-based slicing
    const edges = [
      [0, 1], [1, 3], [3, 2], [2, 0], // bottom face
      [4, 5], [5, 7], [7, 6], [6, 4], // top face
      [0, 4], [1, 5], [2, 6], [3, 7]  // vertical edges
    ];
    
    for (const edge of edges) {
      const [start, end] = edge;
      const startDist = cornerDistance[start];
      const endDist = cornerDistance[end];
      
      if ((startDist >= sliceDistance && endDist <= sliceDistance) ||
          (startDist <= sliceDistance && endDist >= sliceDistance)) {
        
        const t = (sliceDistance - startDist) / (endDist - startDist);
        
        const pos = new THREE.Vector3().lerpVectors(
          this.posCorners[start],
          this.posCorners[end],
          t
        );
        
        const tex = new THREE.Vector3().lerpVectors(
          this.texCorners[start],
          this.texCorners[end],
          t
        );
        
        sliceVertices.push(pos);
        sliceTexCoords.push(tex);
      }
    }
    
    // Add vertices to arrays
    if (sliceVertices.length >= 3) {
      const startIndex = this.points.length / 3;
      
      for (let i = 0; i < sliceVertices.length; i++) {
        this.points.push(
          sliceVertices[i].x,
          sliceVertices[i].y,
          sliceVertices[i].z
        );
        this.texCoords.push(
          sliceTexCoords[i].x,
          sliceTexCoords[i].y,
          sliceTexCoords[i].z
        );
      }
      
      // Create triangles (fan triangulation)
      for (let i = 2; i < sliceVertices.length; i++) {
        this.indexes.push(
          startIndex,
          startIndex + i - 1,
          startIndex + i
        );
      }
    }
  }
  
  updateGeometry() {
    const indexArray = this.geometry.index.array;
    const positionArray = this.geometry.attributes.position.array;
    const texArray = this.geometry.attributes.tex.array;
    
    // Clear arrays
    indexArray.fill(0);
    positionArray.fill(0);
    texArray.fill(0);
    
    // Update with new data
    for (let i = 0; i < this.indexes.length; i++) {
      indexArray[i] = this.indexes[i];
    }
    
    for (let i = 0; i < this.points.length; i++) {
      positionArray[i] = this.points[i];
    }
    
    for (let i = 0; i < this.texCoords.length; i++) {
      texArray[i] = this.texCoords[i];
    }
    
    // Update draw range
    this.geometry.setDrawRange(0, this.indexes.length);
    
    // Mark for update
    this.geometry.index.needsUpdate = true;
    this.geometry.attributes.position.needsUpdate = true;
    this.geometry.attributes.tex.needsUpdate = true;
  }
  
  // Utility methods
  setSize(width, height, depth) {
    this.width = width;
    this.height = height;
    this.depth = depth;
    this.initCorners();
  }
  
  setSliceSpacing(spacing) {
    this.sliceSpacing = spacing;
  }

  setPosition(x, y, z) {
    this.mesh.position.set(x, y, z);
    if (this.debug.active) {
      this.setDebug();
    }
  }
  setRotation(x, y, z) {
    this.mesh.rotation.set(x, y, z);
    if (this.debug.active) {
      this.setDebug();
    }
  }

  // ...existing code...

setDebug() {
  // デバッグが有効な場合のみデバッグUIを作成
  if (this.debug.active) {

    
    // 「VolumetricFire」という名前のフォルダーを作成
    this.debugFolder = this.debug.ui.addFolder('VolumetricFire')
    
    // 位置のデバッグコントロール
    this.debugFolder.add(this.mesh.position, 'x')
      .name('Position X')
      .min(-10).max(10)
      .step(0.01)
      
    this.debugFolder.add(this.mesh.position, 'y')
      .name('Position Y')
      .min(-5).max(15)
      .step(0.01)
      
    this.debugFolder.add(this.mesh.position, 'z')
      .name('Position Z')
      .min(-10).max(30)
      .step(0.01)

    // 回転のデバッグコントロール
    this.debugFolder.add(this.mesh.rotation, 'x')
      .name('Rotation X')
      .min(-Math.PI).max(Math.PI)
      .step(0.01)
      
    this.debugFolder.add(this.mesh.rotation, 'y')
      .name('Rotation Y')
      .min(-Math.PI).max(Math.PI)
      .step(0.01)
      
    this.debugFolder.add(this.mesh.rotation, 'z')
      .name('Rotation Z')
      .min(-Math.PI).max(Math.PI)
      .step(0.01)

    // スケールのデバッグコントロール
    this.debugFolder.add(this.mesh.scale, 'x')
      .name('Scale X')
      .min(0.1).max(3.0)
      .step(0.01)
      
    this.debugFolder.add(this.mesh.scale, 'y')
      .name('Scale Y')
      .min(0.1).max(3.0)
      .step(0.01)
      
    this.debugFolder.add(this.mesh.scale, 'z')
      .name('Scale Z')
      .min(0.1).max(3.0)
      .step(0.01)

    // VolumetricFire特有のパラメータ
    this.debugFolder.add(this, 'sliceSpacing')
      .name('Slice Spacing')
      .min(0.05).max(0.5)
      .step(0.01)

    this.debugFolder.add(this, 'width')
      .name('Fire Width')
      .min(0.1).max(5.0)
      .step(0.1)
      .onChange(() => {
        this.initCorners()
      })

    this.debugFolder.add(this, 'height')
      .name('Fire Height')
      .min(0.1).max(5.0)
      .step(0.1)
      .onChange(() => {
        this.initCorners()
      })

    this.debugFolder.add(this, 'depth')
      .name('Fire Depth')
      .min(0).max(5.0)
      .step(0.1)
      .onChange(() => {
        this.initCorners()
      })

    // フォルダーを最初は閉じておく
    this.debugFolder.close()
  }
}

// ...existing code...
  
  // Dispose method for cleanup
  dispose() {
    this.geometry.dispose();
    this.material.dispose();
    this.material.uniforms.nzw.value.dispose();
    this.material.uniforms.fireProfile.value.dispose();
  }

  update() {
    // カメラが存在するかチェック
    if (!this.camera) {
      return;
    }

    this.updateViewVector();
    this.slice();
    this.updateGeometry();
    this.material.uniforms.time.value = this.time.elapsed;
  }
}